import { CalendarOff, Clock, MessageCircle, Phone, Users, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { DayChips, DayPicker } from "@/components/groups/day-chips";
import { DayStatusBanner } from "@/components/groups/day-status-banner";
import { DriverPanel } from "@/components/groups/driver-panel";
import { RouteLine } from "@/components/groups/route-line";
import { UserAvatar } from "@/components/user-avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import {
  useAddAbsence,
  useLeaveGroup,
  useRemoveAbsence,
  useUpdateMembershipDays,
} from "@/hooks/use-groups";
import { t } from "@/i18n/pl";
import {
  formatDays,
  formatMonthYear,
  formatShortDate,
  formatTime,
  isoDate,
} from "@/lib/format";
import type { GroupDetail } from "@/types/api";

/** "dziś" / "jutro" / "cz 28.08" — the way a crew talks about days. */
function absenceLabel(iso: string): string {
  if (iso === isoDate(0)) return t.absence.today;
  if (iso === isoDate(1)) return t.absence.tomorrow;
  return formatShortDate(iso);
}

/**
 * The hero of the home screen: everything a settled crew member needs at 4:50
 * in the morning — today's status, the times, the meeting spot, and the chat.
 */
export function MyGroupCard({ group }: { group: GroupDetail }) {
  const { user } = useAuth();
  const isDriver = user?.id === group.driver.id;
  const leaveGroup = useLeaveGroup();
  const [leaveOpen, setLeaveOpen] = useState(false);

  // Passenger absences: the mirror of the driver's day status. Only a
  // passenger reports one (for themself); everyone in the crew sees the list.
  const addAbsence = useAddAbsence();
  const removeAbsence = useRemoveAbsence();
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const [absenceDate, setAbsenceDate] = useState(() => isoDate(1));
  const canReportAbsence = !isDriver && group.my_membership_status === "active";

  // Dropping a weekday frees the seat that day and unblocks it for the
  // passenger, so "piątki jeżdżę teraz z kimś innym" needs no leaving.
  const updateDays = useUpdateMembershipDays();
  const [daysOpen, setDaysOpen] = useState(false);
  const [pickedDays, setPickedDays] = useState<number[]>([]);
  const myDays = group.my_membership_days ?? [];
  const canChangeDays = canReportAbsence && myDays.length > 1;
  const daysShrunk = pickedDays.length > 0 && pickedDays.length < myDays.length;

  return (
    <section
      aria-label={t.home.yourCrew}
      className="animate-in fade-in slide-in-from-bottom-3 bg-sidebar text-sidebar-foreground min-w-0 overflow-hidden rounded-2xl shadow-lg duration-500"
    >
      <div className="flex flex-col gap-4 p-5 sm:p-6">
        <header className="flex items-center gap-3">
          <UserAvatar user={group.driver} className="size-12 shadow-md" fallbackClassName="bg-sidebar-primary text-sidebar-primary-foreground text-base font-bold" />
          <div className="min-w-0 flex-1">
            <p className="text-sidebar-foreground/60 text-xs font-semibold tracking-widest uppercase">
              {t.home.yourCrew}
            </p>
            <h2 className="truncate text-lg font-bold">{group.driver.full_name}</h2>
            {/* Own line, never truncated: "Toyota Corolla kombi srebrna" is how
                a passenger recognises the car at 4:50 in the dark. */}
            <p className="text-sidebar-foreground/60 text-sm">{group.car_model}</p>
          </div>
        </header>

        {group.today_status && <DayStatusBanner status={group.today_status} />}

        <dl className="grid grid-cols-2 gap-3">
          <div className="bg-sidebar-accent/60 rounded-xl p-3.5">
            <dt className="text-sidebar-foreground/60 flex items-center gap-1.5 text-xs font-medium">
              <Clock className="size-3.5" aria-hidden /> {t.group.departure}
            </dt>
            <dd className="text-sidebar-primary mt-1 text-3xl font-bold tabular-nums">
              {formatTime(group.departure_time)}
            </dd>
          </div>
          <div className="bg-sidebar-accent/60 rounded-xl p-3.5">
            <dt className="text-sidebar-foreground/60 flex items-center gap-1.5 text-xs font-medium">
              <Clock className="size-3.5" aria-hidden /> {t.group.return}
            </dt>
            <dd className="mt-1 text-3xl font-bold tabular-nums">
              {formatTime(group.return_time)}
            </dd>
          </div>
        </dl>

        <div className="flex flex-col gap-1.5 text-sm">
          <RouteLine from={group.pickup_area} to={group.plant} muted />
          {group.pickup_details && (
            <p className="text-sidebar-foreground/70 pl-6">{group.pickup_details}</p>
          )}
          <DayChips
            days={group.days}
            availability={Object.fromEntries(
              group.day_availability.map((entry) => [entry.day, entry.free]),
            )}
            muted
            className="pl-6"
          />
        </div>

        <div>
          <p className="text-sidebar-foreground/60 mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
            <Users className="size-3.5" aria-hidden />
            {t.group.crew(group.members.length, group.seats_total)}
          </p>
          <ul className="flex flex-col gap-1.5">
            {group.members.map((member) => (
              <li
                key={member.membership_id}
                className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-sm"
              >
                <UserAvatar user={member.user} className="size-7" fallbackClassName="bg-sidebar-accent text-sidebar-foreground text-[11px] font-semibold" />
                <span className="font-medium">{member.user.full_name}</span>
                <span className="text-sidebar-primary text-xs font-semibold">
                  {formatDays(member.days)}
                </span>
                {member.phone && (
                  <a
                    href={`tel:${member.phone.replace(/\s/g, "")}`}
                    className="text-sidebar-foreground/70 flex items-center gap-1 text-xs underline-offset-2 hover:underline"
                  >
                    <Phone className="size-3" aria-hidden />
                    {member.phone}
                  </a>
                )}
                {member.joined_at && (
                  <span className="text-sidebar-foreground/50 text-xs">
                    {t.group.ridingSince(formatMonthYear(member.joined_at))}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {canChangeDays && (
          <p className="flex flex-wrap items-center gap-x-2 text-sm">
            <span className="text-sidebar-foreground/60">{t.group.myDays}:</span>
            <strong className="text-sidebar-primary">{formatDays(myDays)}</strong>
            <Button
              variant="link"
              size="sm"
              className="text-sidebar-foreground h-auto p-0 underline-offset-2"
              onClick={() => {
                setPickedDays(myDays);
                setDaysOpen(true);
              }}
            >
              {t.group.changeDays}
            </Button>
          </p>
        )}

        {group.absences.length > 0 && (
          <div>
            <p className="text-sidebar-foreground/60 mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
              <CalendarOff className="size-3.5" aria-hidden />
              {t.absence.title}
            </p>
            <ul className="flex flex-col gap-1">
              {group.absences.map((absence) => {
                const mine = absence.user.id === user?.id;
                return (
                  <li key={absence.id} className="flex items-center gap-2 text-sm">
                    <span className="text-sidebar-primary w-16 shrink-0 font-semibold tabular-nums">
                      {absenceLabel(absence.date)}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {mine ? t.absence.you : absence.user.full_name}
                    </span>
                    {mine && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent size-7"
                        aria-label={t.absence.withdraw}
                        disabled={removeAbsence.isPending}
                        onClick={() =>
                          removeAbsence.mutate({
                            membershipId: absence.membership_id,
                            absenceId: absence.id,
                          })
                        }
                      >
                        <X className="size-3.5" aria-hidden />
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {isDriver && (
          <div className="border-sidebar-border border-t pt-4">
            <DriverPanel group={group} />
          </div>
        )}

        {/* Wraps on narrow phones: three buttons do not fit in 320px, and a
            button label must never be clipped. */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            asChild
            className="bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 h-11 flex-1 font-semibold"
          >
            <Link to={`/grupy/${group.id}`}>
              <MessageCircle aria-hidden /> {t.chat.title}
            </Link>
          </Button>
          {canReportAbsence && (
            <Button
              variant="outline"
              className="text-foreground h-11"
              onClick={() => {
                // "Tomorrow" is computed when the dialog opens, not when the
                // card mounted — a PWA left open overnight would otherwise
                // file "nie jadę" for a day that has already begun.
                setAbsenceDate(isoDate(1));
                setAbsenceOpen(true);
              }}
            >
              <CalendarOff aria-hidden /> {t.absence.report}
            </Button>
          )}
          {!isDriver && group.my_membership_id && (
            <Button
              variant="ghost"
              className="text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent ml-auto h-11"
              onClick={() => setLeaveOpen(true)}
            >
              {t.group.leave}
            </Button>
          )}
        </div>
      </div>

      <Dialog open={daysOpen} onOpenChange={setDaysOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.group.changeDaysTitle}</DialogTitle>
            <DialogDescription>{t.group.changeDaysBody}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label>{t.days.label}</Label>
            <DayPicker
              allowed={myDays}
              selected={pickedDays}
              onChange={setPickedDays}
              idPrefix="my-day"
            />
            {pickedDays.length === 0 && (
              <p className="text-muted-foreground text-xs">{t.group.changeDaysAllRemoved}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDaysOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              disabled={updateDays.isPending || !daysShrunk || !group.my_membership_id}
              onClick={() => {
                if (!group.my_membership_id) return;
                updateDays.mutate(
                  { membershipId: group.my_membership_id, days: pickedDays },
                  { onSuccess: () => setDaysOpen(false) },
                );
              }}
            >
              {t.group.changeDaysSave}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={absenceOpen} onOpenChange={setAbsenceOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.absence.dialogTitle}</DialogTitle>
            <DialogDescription>{t.absence.dialogBody}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="absence-date">{t.absence.dateLabel}</Label>
            <Input
              id="absence-date"
              type="date"
              min={isoDate(0)}
              max={isoDate(60)}
              value={absenceDate}
              onChange={(event) => setAbsenceDate(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbsenceOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              disabled={addAbsence.isPending || !absenceDate || !group.my_membership_id}
              onClick={() => {
                if (!group.my_membership_id) return;
                addAbsence.mutate(
                  { membershipId: group.my_membership_id, date: absenceDate },
                  { onSuccess: () => setAbsenceOpen(false) },
                );
              }}
            >
              {t.absence.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.group.leaveConfirmTitle}</DialogTitle>
            <DialogDescription>{t.group.leaveConfirmBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              disabled={leaveGroup.isPending}
              onClick={() => {
                if (!group.my_membership_id) return;
                leaveGroup.mutate(
                  { membershipId: group.my_membership_id, wasPending: false },
                  { onSuccess: () => setLeaveOpen(false) },
                );
              }}
            >
              {t.group.leave}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
